"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = withManifest;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _reactMintl = require("@godaddy/react-mintl");
var _react = _interopRequireWildcard(require("react"));
var _hivemindProvider = require("@ux/hivemind-provider");
var _accountDelegation = _interopRequireDefault(require("@ux/account-delegation"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _headerUtil = require("@ux/header-util");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const SkipNavigation = /*#__PURE__*/(0, _react.lazy)(() => Promise.resolve().then(() => _interopRequireWildcard(require( /* webpackChunkName: "skip-navigation" */
'@ux/skip-navigation'))));
const BrowserDeprecationBanner = /*#__PURE__*/(0, _react.lazy)(() => Promise.resolve().then(() => _interopRequireWildcard(require( /* webpackChunkName: "browser-deprecation-banner" */
'@ux/browser-deprecation-banner'))));

/**
 * React Component to render the manifest, initialize user state, traffic and
 * read page configuration. Note: it is preferred to not change this HOC's
 * functionality. Either contribute to the Gasket's HCS plugin or introduce the
 * desired functionality at a lower level. The HOC provides the Header and
 * Footer with data from the PCS, page state and/or configuration.
 * @type {import('@godaddy/gasket-hcs').withManifest}
 * @public
 */
function withManifest(WrappedComponent, {
  renderAccountDelegation = false,
  initCustomerState = false,
  initTraffic = false,
  additionalHeaderMethods = {},
  componentName = 'header'
} = {}) {
  /**
   * Manifest component wrapper.
   * @param {Partial<import('.').ManifestProps>} props Component props
   * @returns {React.ReactElement} Rendered Component.
   */
  function WithManifest({
    shouldAuthenticate = true,
    enableHivemindProvider,
    urls,
    target,
    ...props
  }) {
    let customer = {};
    const guiUrl = _headerUtil.url.fixGuiUrl(urls.gui.href);

    // Get customer state from GUI API.
    customer = (0, _headerUtil.useCustomerDetails)(guiUrl, {
      shouldAuthenticate: shouldAuthenticate || _accountDelegation.default.active,
      privateLabelId: props.privateLabelId,
      preset: props.preset,
      initCustomerState
    });
    if (customer?.customer) {
      // set the loggedIn flag to true if the customer is logged in or if the account delegation is active
      customer.loggedIn = customer.loggedIn || renderAccountDelegation && _accountDelegation.default.active;
    }
    (0, _headerUtil.useTraffic)(props.traffic, {
      initTraffic
    });

    // We need to leverage useState hook to introduce the window object as that
    // prevents hydration issues with server side rendering.
    const [hasDOM, setHasDOM] = (0, _react.useState)(false);
    const fallbackWindow = hasDOM ? global : {};
    const win = target || fallbackWindow;
    (0, _react.useEffect)(() => {
      if (typeof window !== 'undefined') {
        setHasDOM(true);
      }
    }, []);
    const {
      navigation: pageNav = [],
      navigationRight: pageNavRight = [],
      ...pageConfig
    } = (0, _headerUtil.usePageConfig)(win) || {};
    const [navigation, setNavigation] = (0, _react.useState)(pageNav);
    const [navLoadedFromPage, setNavLoadedFromPage] = (0, _react.useState)(false);
    const [hivemindClient, setHivemindClient] = (0, _react.useState)(null);

    // The default value in useState is only set on the first render this causes
    // navigation to be empty since pageNav is not populated until after the
    // first render
    if (!navLoadedFromPage && navigation.length === 0 && pageNav.length > 0) {
      setNavigation(pageNav);
      setNavLoadedFromPage(true);
    }
    (0, _react.useEffect)(() => {
      _headerUtil.events.emit('navigation:updated');
    }, [navigation]);
    const [navigationRight, setNavigationRight] = (0, _react.useState)(pageNavRight);
    (0, _react.useEffect)(() => {
      _headerUtil.events.emit('navigationRight:updated');
    }, [navigationRight]);
    const headerMethods = {
      /**
       * Update header navigation
       * @param {object[]} nav Navigation items
       * @param {boolean} [right] Whether to operate on the right side of the
       * nav (default left)
       * @param {Function} [done] Callback
       */
      updateNavigation(nav, right = false, done) {
        if (typeof right === 'function') {
          done = right;
          right = false;
        }
        if (right) {
          setNavigationRight(nav.slice());
        } else {
          setNavigation(nav.slice());
        }
        if (done) {
          // NOTE: we can't really know for sure whether the effect hook that
          // triggers this event is indeed the particular effect that was
          // triggered by this state change. But this is likely fine. If we see
          // timing issues in the future, we could reconsider this -- perhaps
          // passing along the navigation object itself and comparing it to the
          // one originally passed here.
          _headerUtil.events.once((right ? 'navigationRight' : 'navigation') + ':updated', done);
        }
      },
      ...additionalHeaderMethods
    };
    (0, _react.useEffect)(() => {
      _headerUtil.events.emit('mount', componentName, headerMethods);
      _headerUtil.events.emit(`mount:${componentName}`, headerMethods);
      // discuss with jonathan/daniel/xi about adding this and/or only using
      // window(removing header-util events usage above?)
      // window.dispatchEvent(new CustomEvent('mount', { details: {
      // headerMethods } }))
    }, []);
    (0, _react.useEffect)(() => {
      if (enableHivemindProvider && typeof window !== 'undefined' && window.hivemind) {
        window.hivemind.getClient().then(client => {
          setHivemindClient(client);
        }).catch(err => {
          console.log('unable to getClient for setting Hivemind Client', err);
        });
      }
    }, [setHivemindClient]);
    const includeAccountDelegation = props.features?.accountDelegationBanner !== false && renderAccountDelegation && !!customer?.customer && props.preset !== 'internal-header';
    const skipToContentSelector = props.skipToMainContentLink?.id || 'uxContent';
    const manifestElements = /*#__PURE__*/_react.default.createElement(_reactMintl.IntlProvider, {
      locale: props.market,
      messages: props.messages
    }, /*#__PURE__*/_react.default.createElement("div", {
      className: "skip-nav-spacing"
    }, componentName === 'header' && /*#__PURE__*/_react.default.createElement(_headerUtil.SSRSafeSuspense, null, /*#__PURE__*/_react.default.createElement(_reactMintl.FormattedMessage, {
      id: "Shared:Common:SkipToMainContent"
    }, skipToMainContentString => /*#__PURE__*/_react.default.createElement(SkipNavigation, (0, _extends2.default)({
      skipToId: skipToContentSelector,
      text: props.skipToMainContentLink?.caption || skipToMainContentString
    }, props.skipToMainContentLink?.optionalAttributes))))), includeAccountDelegation && /*#__PURE__*/_react.default.createElement(_accountDelegation.default, {
      market: props.market,
      messages: props.messages,
      action: urls.sso.exitDelegation.href,
      restoreCookie: urls.sso.restoreCookie.href,
      customer: customer.customer
    }), componentName === 'header' && /*#__PURE__*/_react.default.createElement(_headerUtil.SSRSafeSuspense, null, /*#__PURE__*/_react.default.createElement(BrowserDeprecationBanner, {
      supportMatrix: props.supportMatrix,
      whitelistedUserAgents: props.whitelistedUserAgents,
      disableDeprecationBanner: props.disableDeprecationBanner,
      blacklistedBrowsers: props.blacklistedBrowsers,
      urls: urls
    })), /*#__PURE__*/_react.default.createElement(WrappedComponent, (0, _extends2.default)({}, props, pageConfig, (0, _headerUtil.useHivemind)(win, props.hivemind), {
      urls: urls,
      orderId: (0, _headerUtil.useOrderDetails)(win),
      customer: customer,
      navigation: navigation,
      navigationRight: navigationRight,
      headerMethods: headerMethods
    })));
    return hivemindClient ? /*#__PURE__*/_react.default.createElement(_hivemindProvider.HivemindProvider, {
      client: hivemindClient
    }, manifestElements) : manifestElements;
  }
  WithManifest.propTypes = {
    blacklistedBrowsers: _propTypes.default.arrayOf(_propTypes.default.shape({
      browser: _propTypes.default.string.isRequired,
      version: _propTypes.default.number.isRequired
    })),
    disableDeprecationBanner: _propTypes.default.bool,
    hivemind: _propTypes.default.object,
    enableHivemindProvider: _propTypes.default.bool,
    features: _propTypes.default.shape({
      accountDelegationBanner: _propTypes.default.bool
    }),
    market: _propTypes.default.string.isRequired,
    messages: _propTypes.default.object.isRequired,
    privateLabelId: _propTypes.default.number,
    preset: _propTypes.default.string,
    shouldAuthenticate: _propTypes.default.bool,
    skipToMainContentLink: _propTypes.default.shape({
      id: _propTypes.default.string,
      caption: _propTypes.default.string,
      optionalAttributes: _propTypes.default.object
    }),
    supportMatrix: _propTypes.default.object.isRequired,
    target: _propTypes.default.object,
    traffic: _propTypes.default.bool,
    urls: _propTypes.default.object.isRequired,
    whitelistedUserAgents: _propTypes.default.arrayOf(_propTypes.default.string)
  };
  WithManifest.propTypes = {
    ...WithManifest.propTypes,
    ...WrappedComponent.propTypes
  };
  WithManifest.displayName = `WithManifest(${WrappedComponent.displayName || WrappedComponent.name})`;
  return WithManifest;
}